// src/pages/index.js (Dashboard) — MUI-based dropdown (stable)
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Container,
  Menu,
  MenuItem,
  Checkbox,
  Typography,
  Stack,
} from "@mui/material";

// Import Components
import MiniWidgets from "./MiniWidgets";
import RevenueAnalytics from "./RevenueAnalytics";
import BlockedPolicies from "./BlockedPolicies";
import PolicyViolationTrends from "./PolicyViolationTrends";
import RiskAnalysis from "./RiskAnalysis";
import PeakHours from "./PeakHours";
import TopAccessedDomains from "./TopDomains";
import CustomChartsWidget from "./CustomChartsWidget";
import { request } from "../../utils/request";

const LOCALSTORAGE_KEY = "dashboardWidgetVisibility";

const Dashboard = () => {
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.superadmin === true;
  const userPermissions = currentUser.permissions || [];
  const hasDashboardAccess = isSuperAdmin || userPermissions.some(p => p.includes('DashboardRead'));

  // default visibility
  const defaultVisibility = {
    miniWidgets: true,
    recentlyActivity: false,
    latestTransactions: false,
    riskAnalysis: true,
    revenueAnalytics: false,
    blockedPolicies: true,
    PeakHours: true,
    TopAccessedDomains: true,
    PolicyViolationTrends: true,
    customCharts: true,
  };

  const [visibility, setVisibility] = useState(defaultVisibility);
  const [hasSavedCustomCharts, setHasSavedCustomCharts] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  // MUI Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    // load or initialize visibility
    try {
      const raw = localStorage.getItem(LOCALSTORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setVisibility({ ...defaultVisibility, ...parsed });
      } else {
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(defaultVisibility));
      }
    } catch (err) {
      console.error("Error reading widget visibility from localStorage:", err);
      setVisibility(defaultVisibility);
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(defaultVisibility));
    }
  }, []);

  useEffect(() => {
    const loadCustomCharts = async () => {
      try {
        const response = await request("customcharts", { method: "GET" });
        setHasSavedCustomCharts(Array.isArray(response?.data) && response.data.length > 0);
      } catch (error) {
        console.error("Error reading saved custom charts:", error);
        setHasSavedCustomCharts(false);
      }
    };

    loadCustomCharts();
  }, []);

  // show dialog once after 1 minute (if never shown)
  useEffect(() => {
    const lastShown = localStorage.getItem("lastDialogShown");
    if (!lastShown) {
      const timer = setTimeout(() => {
        setShowDialog(true);
        localStorage.setItem("lastDialogShown", Date.now().toString());
      }, 60000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleCloseDialog = () => setShowDialog(false);

  if (!hasDashboardAccess) {
    return (
      <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 5 }}>
        <Typography variant="h5" sx={{ color: 'var(--slate)', fontFamily: 'var(--font-head)', textAlign: 'center' }}>
          Access Denied. You do not have permission to view the Dashboard.
        </Typography>
      </Box>
    );
  }

  const handleToggle = (key) => {
    const next = { ...visibility, [key]: !visibility[key] };
    setVisibility(next);
    try {
      localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(next));
    } catch (err) {
      console.error("Error saving widget visibility to localStorage:", err);
    }
  };

  // widget list + labels
  const widgetDefinitions = [
    { key: "miniWidgets", label: "Overview Charts", node: <MiniWidgets /> },
    { key: "riskAnalysis", label: "Risk Analysis", node: <RiskAnalysis /> },
    { key: "blockedPolicies", label: "Blocked Policies", node: <BlockedPolicies /> },
    { key: "PeakHours", label: "Peak Hours", node: <PeakHours /> },
    { key: "revenueAnalytics", label: "Login Sessions Map", node: <RevenueAnalytics /> },
    { key: "TopAccessedDomains", label: "Top Accessed Domains", node: <TopAccessedDomains /> },
    { key: "PolicyViolationTrends", label: "Policy Violation Trends", node: <PolicyViolationTrends /> },
    { key: "customCharts", label: "Insight Builder", node: <CustomChartsWidget /> },
  ];

  // visible widget nodes in order (excluding miniWidgets as it's rendered separately)
  const visibleWidgets = widgetDefinitions
    .filter((w) => w.key !== "miniWidgets" && visibility[w.key] && (w.key !== "customCharts" || hasSavedCustomCharts))
    .map((w) => w.node);

  const placeholdersNeeded = Math.max(0, 6 - visibleWidgets.length);

  // menu handlers
  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  // helpers for select all / clear
  const selectAll = () => {
    const allOn = widgetDefinitions.reduce((acc, w) => ({ ...acc, [w.key]: true }), {});
    setVisibility(allOn);
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(allOn));
  };
  const clearAll = () => {
    const none = widgetDefinitions.reduce((acc, w) => ({ ...acc, [w.key]: false }), {});
    setVisibility(none);
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(none));
  };

  return (
    <>
      <Box className="page-content" sx={{ flexGrow: 1 }}>
        <Container maxWidth="xl" sx={{ py: 2 }}>
          {/* Header row */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            mb={2}
            flexWrap="wrap"
            gap={2}
          >

            {/* View button with menu */}
            <Box ml={2} mt={0}>
              <Button
                variant="contained"
                onClick={openMenu}
                aria-controls={menuOpen ? "widgets-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? "true" : undefined}
                sx={{
                  backgroundColor: "var(--blue)",
                  color: "var(--canvas)",
                  fontFamily: "var(--font-head)",
                  fontWeight: 700,
                  fontSize: "14px",
                  borderRadius: "6px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "var(--blue)",
                    boxShadow: "0 4px 12px rgba(31, 111, 235, 0.2)",
                  },
                }}
              >
                Widgets
              </Button>

              <Menu
                id="widgets-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={closeMenu}
                disableScrollLock
                PaperProps={{
                  sx: {
                    minWidth: 280, // Increased to prevent collision
                    p: "12px",
                    borderRadius: "12px",
                    border: "1px solid var(--hairline)",
                    boxShadow: "0 4px 12px rgba(11, 37, 69, 0.08), 0 1px 2px rgba(11, 37, 69, 0.04)",
                    backgroundColor: "var(--canvas)",
                    mt: 1,
                  },
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb="12px" gap="16px">
                  <Typography 
                    sx={{ 
                      fontSize: "14px", 
                      m: 0, 
                      color: "var(--blue)", 
                      fontFamily: "var(--font-head)", 
                      fontWeight: 700,
                      whiteSpace: "nowrap"
                    }}
                  >
                    Show Widgets
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button
                      size="small"
                      onClick={selectAll}
                      disableRipple
                      sx={{ 
                        p: 0, 
                        minWidth: "auto", 
                        fontSize: "13px", 
                        color: "var(--blue)", 
                        fontFamily: "var(--font-head)", 
                        fontWeight: 700,
                        textTransform: "none",
                        "&:hover": { backgroundColor: "transparent", textDecoration: "underline" }
                      }}
                    >
                      Select all
                    </Button>
                    <Button
                      size="small"
                      onClick={clearAll}
                      disableRipple
                      sx={{ 
                        p: 0, 
                        minWidth: "auto", 
                        fontSize: "13px", 
                        color: "var(--slate)", 
                        fontFamily: "var(--font-body)", 
                        fontWeight: 400,
                        textTransform: "none",
                        "&:hover": { backgroundColor: "transparent", color: "var(--navy)" }
                      }}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Box>

                <Box sx={{ borderTop: "1px solid var(--hairline)", pt: "8px", display: "flex", flexDirection: "column", gap: "2px" }}>
                  {widgetDefinitions.map((w) => {
                    const isChecked = !!visibility[w.key];
                    return (
                      <MenuItem 
                        key={w.key} 
                        onClick={() => handleToggle(w.key)}
                        sx={{ 
                          borderRadius: "6px",
                          padding: "6px 8px",
                          minHeight: "36px",
                          transition: "background-color 0.15s ease",
                          '&:hover': { backgroundColor: 'var(--mist)' } 
                        }}
                      >
                        <Checkbox
                          checked={isChecked}
                          size="small"
                          disableRipple
                          sx={{ 
                            p: 0, 
                            mr: "12px", 
                            color: "var(--slate)",
                            '&.Mui-checked': {
                              color: "var(--blue)",
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: "20px",
                              borderRadius: "4px"
                            }
                          }}
                        />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: isChecked ? "var(--navy)" : "var(--slate)",
                            fontWeight: isChecked ? 600 : 400,
                            fontFamily: "var(--font-body)",
                            fontSize: "15px"
                          }}
                        >
                          {w.label}
                        </Typography>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Menu>
            </Box>
          </Box>

          {/* Top row: Primary overview metrics */}
          {visibility.miniWidgets && (
            <MiniWidgets />
          )}

          {/* Dashboard grid (3x2 layout for remaining 6 widgets) */}
          <div className="widget-grid">
            {visibleWidgets.length === 0 && (
              <div style={{ gridColumn: "1 / -1", padding: "16px" }}>
                <Box className="bg-mist text-secondary global-card" sx={{ minHeight: 'auto', p: 3, textAlign: 'center' }}>
                  No widgets selected. Use the <strong>Widgets</strong> button to show Graphs and Maps.
                </Box>
              </div>
            )}

            {visibleWidgets.map((W, idx) => (
              <div key={idx} className="widget-card">
                <Box sx={{ height: "100%", minWidth: 0, overflow: "hidden" }}>{W}</Box>
              </div>
            ))}

            {Array.from({ length: placeholdersNeeded }).map((_, i) => (
              <div key={`ph-${i}`} style={{ visibility: "hidden", height: "100%" }} />
            ))}
          </div>
        </Container>
      </Box>
    </>
  );
};

export default Dashboard;